import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { Vendor } from '../shared/dto/vendor-management/vendor';
import { RequestService } from '../shared/services/request-service';
import { HttpClient } from '@angular/common/http';
import { MarginAnalysisRequest } from '../shared/dto/finances/margin-analysis-request';
import { MarginAnalysisBulkRequest } from '../shared/dto/finances/margin-analysis-bulk-request';
import { MarginAnalysisResponse } from '../shared/dto/finances/margin-analysis-response';
import { TaskRate } from '../shared/dto/projects/task-rate';
import { TaskAssignmentDetails, VendorAssignmentTransMem } from '../shared/response/task-assignment-details';
import { VendorTMLanguagePairSummary } from '../shared/dto/translation-memory/document-trans-mem-language';
import { EditVendorWordCountDetail } from '../shared/dto/projects/edit-vendor-word-count-detail';

@Injectable()
export class AssignmentService {

  constructor(private httpClient: HttpClient,
              private requestService: RequestService) {
  }

  public getVendorForLanguagePairServiceByTaskIds(taskIds: number[]): Promise<Vendor[]> {
    const url = '/api/assignment/vendor/bulk/assign/active/service';
    const headers = this.requestService.buildHttpHeaders();
    return this.httpClient.post<Vendor[]>(url, taskIds, { headers: headers })
      .toPromise();
  }

  public getLanguagePairServiceByTaskIdsAndVendorId(taskIds: number[], vendorId: number): Promise<Vendor> {
    const url = `/api/assignment/vendor/${vendorId}/tasks/filter/service`;
    const headers = this.requestService.buildHttpHeaders();
    return this.httpClient.post<Vendor>(url, taskIds, { headers: headers })
      .toPromise();
  }

  public getAssignableVendorMargins(request: MarginAnalysisRequest): Observable<MarginAnalysisResponse> {
    const url = '/api/task/assignment/margins';
    const headers = this.requestService.buildHttpHeaders();
    return this.httpClient.post<MarginAnalysisResponse>(url, request, { headers: headers });
  }

  public getAssignableVendorMarginsBulk(request: MarginAnalysisBulkRequest): Observable<MarginAnalysisResponse[]> {
    const url = '/api/task/assignment/margins/bulk';
    const headers = this.requestService.buildHttpHeaders();
    return this.httpClient.post<MarginAnalysisResponse[]>(url, request, { headers: headers });
  }

  public getFilingRateTotal(taskRate: TaskRate): Observable<TaskRate> {
    const url = '/api/task/calculate-filing-rate';
    const headers = this.requestService.buildHttpHeaders();

    return this.httpClient.post<TaskRate>(url, taskRate, { headers: headers });
  }

  public getTaskAssignmentDetails(taskId: number): Observable<TaskAssignmentDetails> {
    const url = `/api/task/${taskId}/assignment-details`;
    const headers = this.requestService.buildHttpHeaders();

    return this.httpClient.get<TaskAssignmentDetails>(url, { headers: headers });
  }

  public calculateTmVendorWeights(
    taskId: number,
    vendorId: number,
    assignmentTm: VendorAssignmentTransMem,
  ): Observable<VendorAssignmentTransMem> {
    const url = `/api/task/${taskId}/translation/memory/calculate/vendor/${vendorId}`;
    const headers = this.requestService.buildHttpHeaders();

    return this.httpClient.post<VendorAssignmentTransMem>(url, assignmentTm, { headers: headers });
  }

  public calculateModifiedTmVendorWeights(
    taskId: number,
    assignmentTm: VendorAssignmentTransMem,
  ): Observable<VendorAssignmentTransMem> {
    const url = `/api/task/${taskId}/translation/memory/calculate`;
    const headers = this.requestService.buildHttpHeaders();

    return this.httpClient.post<VendorAssignmentTransMem>(url, assignmentTm, { headers: headers });
  }

  public getTaskTmDetails(taskId: number): Observable<VendorAssignmentTransMem> {
    const url = '/api/task/translation/memory';
    const headers = this.requestService.buildHttpHeaders();

    return this.httpClient.post<VendorAssignmentTransMem[]>(url, [taskId], { headers: headers })
      .pipe(
        map(response => response[0])
      );
  }

  public getEditWordCountInfo(taskId: number): Observable<EditVendorWordCountDetail> {
    const url = `/api/task/${taskId}/edit-word-count-info`;
    const headers = this.requestService.buildHttpHeaders();

    return this.httpClient.get<EditVendorWordCountDetail>(url, { headers });
  }

  public getTranslationMemoryDetails(taskId: number): Observable<VendorAssignmentTransMem> {
    const url = `/api/task/${taskId}/translation/memory`;
    const headers = this.requestService.buildHttpHeaders();

    return this.httpClient.get<VendorAssignmentTransMem>(url, { headers });
  }

  public updateTranslationMemory(taskId: number, request: VendorAssignmentTransMem): Observable<VendorTMLanguagePairSummary> {
    const url = `api/task/${taskId}/translation/memory`;
    const headers = this.requestService.buildHttpHeaders();

    return this.httpClient.put<VendorTMLanguagePairSummary>(url, request, { headers: headers });
  }
}
